import React, { useState } from 'react';
import EuropeMap from './components/EuropeMap';
import CaseSelector from './components/CaseSelector';
import ResultsDisplay from './components/ResultsDisplay';
import RequestLogger from './components/RequestLogger';
import CaseComparison from './components/CaseComparison';
import AssumptionsModal from './components/AssumptionsModal';
import CookieConsent from './components/CookieConsent';
import Footer from './components/Footer';
import NZIABox from './components/NZIABox';
import Case5Detailed from './components/Case5Detailed';
import FeedbackButton from './components/FeedbackButton';
import { europeanCities, availableCases } from './data/casesData';

function App() {
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [showRequestNotification, setShowRequestNotification] = useState(false);
  const [showAssumptionsModal, setShowAssumptionsModal] = useState(false);
  const [builderResults, setBuilderResults] = useState(null);
  const [viewMode, setViewMode] = useState('predefined'); // 'predefined', or 'case5'
  const [comparisonScenarios, setComparisonScenarios] = useState([]); // Store scenarios to add to comparison

  const handleCitySelect = (cityName) => {
    setSelectedCities(prev => {
      if (prev.includes(cityName)) {
        return prev.filter(city => city !== cityName);
      } else {
        return [...prev, cityName];
      }
    });
    // When cities change, check if combination exists in cases
    // If not, selectedCase should remain for comparison, but show "request analysis" message
  };

  const handleCaseSelect = (caseKey) => {
    setSelectedCase(caseKey);
  };

  const handleNewRequest = (request) => {
    setShowRequestNotification(true);
    setTimeout(() => setShowRequestNotification(false), 5000);
  };

  const handleAddToComparison = (selections) => {
    setComparisonScenarios(prev => [...prev, selections]);
  };

  const selectedCaseData = selectedCase ? availableCases[selectedCase] : null;

  // Check if selected cities match the selected case
  const citiesMatchCase = selectedCaseData && selectedCities.length > 0
    ? selectedCities.every(city => selectedCaseData.cities?.includes(city))
    : true;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-yellow-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b-4" style={{ borderColor: '#E78E42' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 sm:py-6 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(to right, #0A3C80, #E78E42)' }}>
                EU Solar Manufacturing Competitiveness
              </h1>
              <p className="mt-2 text-sm sm:text-base lg:text-lg" style={{ color: '#0A3C80' }}>
                Explore optimization scenarios for solar PV manufacturing across Europe
              </p>
            </div>
            {/* Logos and Assumptions Button */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 lg:gap-6 lg:ml-8">
              <button
                onClick={() => setShowAssumptionsModal(true)}
                className="text-white px-3 py-2 sm:px-4 rounded-lg font-semibold shadow-md hover:shadow-lg transition-shadow flex items-center gap-2 text-sm sm:text-base"
                style={{ backgroundImage: 'linear-gradient(to right, #E78E42, #EAC144)' }}
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                  <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                </svg>
                <span className="hidden sm:inline">Assumptions</span>
                <span className="sm:hidden">Data</span>
              </button>
              <a
                href="https://www.becquerelinstitute.eu/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                <img
                  src="/BI.png"
                  alt="Becquerel Institute"
                  className="h-16 sm:h-24 lg:h-32 object-contain"
                />
              </a>
              <a
                href="https://acap.org.au/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                <img
                  src="/ACAP.png"
                  alt="Australian Centre for Advanced Photovoltaics"
                  className="h-10 sm:h-12 lg:h-16 object-contain"
                />
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Notification */}
      {showRequestNotification && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          ✓ Your optimization request has been submitted! We'll work on it and provide results.
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Scope Section */}
        <div className="mb-6 sm:mb-8 border-2 rounded-xl p-4 sm:p-6 shadow-xl" style={{ backgroundImage: 'linear-gradient(to right, rgba(10, 60, 128, 0.05), rgba(231, 142, 66, 0.05))', borderColor: '#0A3C80' }}>
          <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
            <div className="flex-shrink-0">
              <svg className="w-8 h-8 sm:w-10 sm:h-10" style={{ color: '#E78E42' }} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
              </svg>
            </div>
            <div className="flex-1">
              <h2 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3 text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(to right, #0A3C80, #E78E42)' }}>
                Understanding EU Solar Manufacturing Competitiveness
              </h2>
              <div className="space-y-2 sm:space-y-3 text-sm sm:text-base text-gray-700">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#E78E42' }}></div>
                  <p>
                    <strong className="font-semibold" style={{ color: '#0A3C80' }}>Minimum Sustainable Price (MSP):</strong> The price needed to cover the cost of production plus a profit margin for a sustainable manufacturing business. This tool analyzes MSP across different manufacturing stages and geographic locations to identify competitive scenarios for EU solar manufacturing.
                  </p>
                </div>
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#EAC144' }}></div>
                  <p>
                    <strong className="font-semibold" style={{ color: '#0A3C80' }}>Net-Zero Industry Act (NZIA) Impact:</strong> The EU's NZIA aims to strengthen European manufacturing capacity in strategic clean technologies. This analysis explores how different manufacturing configurations affect competitiveness under current and proposed regulatory frameworks.
                  </p>
                </div>
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#0A3C80' }}></div>
                  <p>
                    <strong className="font-semibold" style={{ color: '#0A3C80' }}>Optimization Scenarios:</strong> Explore over 4,000 pre-computed scenarios across 8 european locations analyzing different geographic distributions of the solar PV supply chain (Polysilicon → Ingot/Wafer → Cell → Module) based on 1,000 Monte Carlo iterations each, or compare 7 predefined representative cases.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mode Toggle */}
        <div className="mb-6 flex justify-center">
          <div className="inline-flex rounded-lg border-2 p-1 w-full sm:w-auto" style={{ borderColor: '#E78E42', backgroundColor: 'rgba(231, 142, 66, 0.05)' }}>
            <button
              onClick={() => setViewMode('predefined')}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md font-semibold transition-all text-xs sm:text-base ${
                viewMode === 'predefined'
                  ? 'text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              style={viewMode === 'predefined' ? { backgroundImage: 'linear-gradient(to right, #E78E42, #EAC144)' } : {}}
            >
              Predefined Cases
            </button>
            <button
              onClick={() => setViewMode('case5')}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md font-semibold transition-all text-xs sm:text-base ${
                viewMode === 'case5'
                  ? 'text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              style={viewMode === 'case5' ? { backgroundImage: 'linear-gradient(to right, #E78E42, #EAC144)' } : {}}
            >
              Made in EU
            </button>
          </div>
        </div>

        {viewMode === 'case5' ? (
          /* Case 5 Detailed Analysis Mode */
          <div className="mb-8">
            <Case5Detailed />
            <a
              href="/IBC4EU"
              className="block mt-8 border-2 rounded-xl p-4 sm:p-6 shadow-xl hover:shadow-2xl transition-shadow"
              style={{
                backgroundImage: 'linear-gradient(to right, rgba(10, 60, 128, 0.05), rgba(231, 142, 66, 0.05))',
                borderColor: '#0A3C80',
              }}
            >
              <div className="flex items-center gap-4 sm:gap-6">
                <img
                  src="/ibc4u-logo.png"
                  alt="IBC4EU"
                  className="h-12 sm:h-16 object-contain flex-shrink-0"
                />
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-bold mb-1" style={{ color: '#0A3C80' }}>
                    Explore the EU PV Industrial Trajectory Model →
                  </h3>
                  <p className="text-sm text-gray-700">
                    From Minimum Sustainable Price to industrial buildout: model EU module/cell/wafer
                    capacity, CAPEX, workforce, and policy timelines through 2050.
                  </p>
                </div>
              </div>
            </a>
          </div>
        ) : (
          /* Predefined Cases Mode */
          <>
            {/* Top Row: Map and Case Selection Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-6 lg:mb-8">
              {/* Map Section */}
              <div className="bg-white p-4 sm:p-6 rounded-xl shadow-xl border border-gray-200 hover:shadow-2xl transition-shadow duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-2 h-8 rounded-full" style={{ backgroundImage: 'linear-gradient(to bottom, #E78E42, #EAC144)' }}></div>
                  <h2 className="text-2xl font-bold" style={{ color: '#000755' }}>Manufacturing Locations</h2>
                </div>
                <p className="text-gray-600 mb-4 text-sm">
                  View potential manufacturing locations across Europe. Cities highlighted in green are used in the selected case.
                </p>
                <EuropeMap
                  cities={europeanCities}
                  selectedCities={selectedCities}
                  onCitySelect={handleCitySelect}
                  highlightedCities={selectedCaseData?.cities || []}
                  disabled={true}
                />
                <div className="mt-4 p-4 rounded-lg border" style={{ backgroundImage: 'linear-gradient(to right, rgba(231, 142, 66, 0.1), rgba(234, 193, 68, 0.1))', borderColor: '#E78E42' }}>
                  <h4 className="font-semibold mb-2 flex items-center gap-2" style={{ color: '#000755' }}>
                    <svg className="w-5 h-5" style={{ color: '#E78E42' }} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    Selected Cities:
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedCities.length === 0 ? (
                      <span className="text-gray-500 italic text-sm">No cities selected</span>
                    ) : (
                      selectedCities.map(city => (
                        <span
                          key={city}
                          className="text-white px-4 py-1.5 rounded-full text-sm font-medium shadow-md hover:shadow-lg transition-shadow"
                          style={{ backgroundImage: 'linear-gradient(to right, #0A3C80, #000755)' }}
                        >
                          {city}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Case Selection */}
              <CaseSelector
                selectedCase={selectedCase}
                onCaseSelect={handleCaseSelect}
              />
            </div>

            {/* NZIA Compliance Box - Full Width */}
            <div>
              <NZIABox
                selectedCase={selectedCase}
                caseData={selectedCaseData}
              />
            </div>
          </>
        )}

        {/* Case Comparison Chart (only in predefined mode) */}
        {viewMode === 'predefined' && (
          <div className="mt-12">
            <CaseComparison />
          </div>
        )}

        {/* Info Section */}
        <div className="mt-8 border-2 rounded-xl p-6 sm:p-8 shadow-xl" style={{ backgroundImage: 'linear-gradient(to bottom right, rgba(231, 142, 66, 0.1), rgba(234, 193, 68, 0.1))', borderColor: '#E78E42' }}>
          <div className="flex items-center gap-3 mb-4">
            <svg className="w-8 h-8" style={{ color: '#E78E42' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <h3 className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(to right, #0A3C80, #E78E42)' }}>
              About the Optimization Cases
            </h3>
          </div>
          <div className="text-gray-700 space-y-4">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 mt-1 flex-shrink-0" style={{ color: '#0A3C80' }} fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
              </svg>
              <div>
                <strong className="text-gray-900">Manufacturing Steps:</strong>
                <p className="text-gray-600 mt-1">Polysilicon → Ingot/Wafer → Cell → Module</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 mt-1 flex-shrink-0" style={{ color: '#E78E42' }} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M12 1.586l-4 4v12.828l4-4V1.586zM3.707 3.293A1 1 0 002 4v10a1 1 0 00.293.707L6 18.414V5.586L3.707 3.293zM17.707 5.293L14 1.586v12.828l2.293 2.293A1 1 0 0018 16V6a1 1 0 00-.293-.707z" clipRule="evenodd" />
              </svg>
              <div>
                <strong className="text-gray-900">Regions:</strong>
                <p className="text-gray-600 mt-1">European Union locations including Spain, France, Italy, Germany, and Sweden across 8 cities</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 mt-1 flex-shrink-0" style={{ color: '#EAC144' }} fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
              </svg>
              <div>
                <strong className="text-gray-900">Analysis Options:</strong>
                <p className="text-gray-600 mt-1">Interactive builder with over 4,000 supply chain combinations across 8 european locations, or compare 7 predefined representative scenarios - all based on 1,000 Monte Carlo iterations</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Assumptions Modal */}
      <AssumptionsModal
        isOpen={showAssumptionsModal}
        onClose={() => setShowAssumptionsModal(false)}
      />

      {/* Footer */}
      <Footer />

      {/* Cookie Consent */}
      <CookieConsent />

      {/* Feedback Button */}
      <FeedbackButton />
    </div>
  );
}

export default App;
